import React, { useEffect, useRef } from 'react';
import { SyncOutlined, CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { api } from '../../services/api';
import { useQuery } from '@tanstack/react-query';

interface PingResult {
  ok: boolean;
  ms: number | null;
  error: boolean;
  skipped?: boolean;
}

const AppFooter: React.FC = () => {
  const pingTimeoutRef = useRef<NodeJS.Timeout>();
  const isDocumentVisible = useRef(true);

  // Handle document visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      isDocumentVisible.current = !document.hidden;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (pingTimeoutRef.current) {
        clearTimeout(pingTimeoutRef.current);
      }
    };
  }, []);

  const { data, isFetching, isError, refetch } = useQuery<PingResult>({
    queryKey: ['dbPing'],
    queryFn: async () => {
      // Don't ping if document is not visible
      if (!isDocumentVisible.current) {
        return { ok: false, ms: null, error: true, skipped: true };
      }

      const start = Date.now();
      try {
        const res = await api.get('/dbExchange/ping');
        const ok = res?.data?.status === 'ok';
        return { 
          ok, 
          ms: ok ? Date.now() - start : null, 
          error: !ok 
        };
      } catch (error) {
        console.error('Ping error:', error);
        return { ok: false, ms: null, error: true };
      }
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 2,
    retryDelay: 5000,
    staleTime: 10000,
    gcTime: 0
  });

  // Manual ping with backoff on error
  useEffect(() => {
    if (!isDocumentVisible.current) {
      return;
    }

    const scheduleNextPing = (delay: number) => {
      if (pingTimeoutRef.current) {
        clearTimeout(pingTimeoutRef.current);
      }
      pingTimeoutRef.current = setTimeout(() => {
        refetch();
      }, delay);
    };

    // Initial ping
    scheduleNextPing(0);

    // Set up interval for pings when online
    const pingInterval = setInterval(() => {
      if (isDocumentVisible.current && navigator.onLine) {
        refetch();
      }
    }, 30000); // Every 30 seconds

    return () => {
      clearInterval(pingInterval);
      if (pingTimeoutRef.current) {
        clearTimeout(pingTimeoutRef.current);
      }
    };
  }, [refetch]);

  const isOnline = typeof navigator === 'undefined' || navigator.onLine;
  
  // Determine ping status
  const dbPing: 'ok' | 'error' | 'pending' = 
    !isOnline || isError || data?.error
      ? 'error'
      : isFetching && !data
      ? 'pending'
      : data?.ok
      ? 'ok'
      : 'pending';

  const pingTime = data?.ms ?? null;

  return (
    <div className="footer-container" style={{ position: 'relative', minHeight: 48, padding: '8px 0', color: 'rgba(0, 0, 0, 0.45)' }}>
      <p style={{ margin: 0, textAlign: 'center' }}>
        © {new Date().getFullYear()} Common Construction Data Environment
      </p>
      <div style={{ position: 'absolute', right: 24, bottom: 8, textAlign: 'right' }}>
        <Tooltip 
          title={
            !isDocumentVisible.current
              ? 'Connection check paused (tab in background)'
              : dbPing === 'pending'
                ? 'Checking connection...'
                : dbPing === 'ok'
                  ? `DB Connection OK${pingTime ? ` (${pingTime} ms)` : ''}`
                  : 'DB connection error!'
          }
        >
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontSize: 16 }}>
            {dbPing === 'pending' && <SyncOutlined spin style={{ color: '#faad14' }} />}
            {dbPing === 'ok' && <CheckCircleTwoTone twoToneColor="#52c41a" />}
            {dbPing === 'error' && <CloseCircleTwoTone twoToneColor="#ff4d4f" />}
            <span 
              style={{ 
                marginLeft: 6, 
                fontSize: 13, 
                color: dbPing === 'ok' 
                  ? '#52c41a' 
                  : dbPing === 'pending' 
                    ? '#faad14' 
                    : '#ff4d4f' 
              }}
            >
              DB
            </span>
            {dbPing === 'ok' && pingTime !== null && (
              <span style={{ marginLeft: 4, fontSize: 11, color: '#888' }}>
                ({pingTime} ms)
              </span>
            )}
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default AppFooter;
 